/**
 * Premium Section Renderer — schema-driven renderer for the 12+ new premium
 * section types (hero, features, split, editorial, stats, logos, team,
 * services, directory, testimonials, timeline, events, cta).
 *
 * Used as a fall-through by both:
 *   - DashboardStyles.PreviewBlock (live preview pane)
 *   - DashboardContent.renderBlock (drag-and-drop canvas)
 *
 * Returns JSX when the block type is recognized as premium, otherwise null
 * (so the legacy renderer continues to handle it).
 */
import React from 'react';
import { SafeImg } from '../SafeImg';
import { getWbButtonAttrs } from '../sqs/WbButton';

const FONT_FALLBACK = "'Inter', 'Helvetica Neue', system-ui, sans-serif";

function _f(font) { return font ? `'${font}', ${FONT_FALLBACK}` : FONT_FALLBACK; }

/** Resolve a per-block theme from the block.theme_id and the site palette. */
function resolveTheme(block, palette, headingFont, radius) {
  const [bg, soft, accent, deep, ink] = palette || ['#FAFAFA', '#E5E7EB', '#8B5CF6', '#1E1B4B', '#0A0A23'];
  return { bg, soft, accent, deep, ink, headingFont, radius: radius ?? 8 };
}

function Btn({ text, link, primary, theme, sharp, buttonIndex, color, field = 'buttons' }) {
  const r = sharp ? 0 : theme.radius;
  return (
    <a href={link || '#'} className="inline-block px-7 py-3 text-sm font-bold tracking-wider uppercase transition-all hover:opacity-90 wb-sortable-item"
       {...getWbButtonAttrs(buttonIndex, field)}
       style={{ borderRadius: r, background: color || (primary ? theme.accent : 'transparent'), border: (primary || color) ? 'none' : `1px solid ${theme.ink}`, color: (primary || color) ? '#fff' : theme.ink }}>
      {text}
    </a>
  );
}

function H1({ children, theme, size = 'text-5xl md:text-6xl', testid }) {
  return (
    <h1 data-testid={testid} className={`${size} font-bold leading-[1.05] tracking-tight whitespace-pre-line`} style={{ fontFamily: _f(theme.headingFont), color: theme.ink }}>{children}</h1>
  );
}

function H2({ children, theme, className = '', testid }) {
  return (
    <h2 data-testid={testid} className={`text-3xl md:text-4xl font-bold leading-tight tracking-tight ${className}`} style={{ fontFamily: _f(theme.headingFont), color: theme.ink }}>{children}</h2>
  );
}

function Eyebrow({ children, theme, testid }) {
  if (!children) return null;
  return <p data-testid={testid} className="text-xs uppercase tracking-[0.22em] opacity-70 mb-3" style={{ color: theme.ink }}>{children}</p>;
}

// Helper: per-block testid factory — `tid('title')` → 'inline-<blockId>-title'.
// Used by `useFluidTextDrag` to make individual elements free-position-draggable
// matching the Hero/About inline-edit behaviour. Iter 162.
const _tid = (block) => (field) => block?.id ? `inline-${block.id}-${field}` : undefined;

// ─────────────────────────────────────────────────────────────────────────
// HERO — 6 layouts
// ─────────────────────────────────────────────────────────────────────────
function HeroBlock({ block, theme }) {
  const c = block.content || {};
  const layout = block.layout || 'cinematic-fullwidth';
  const buttons = c.buttons || [];
  const tid = _tid(block);

  if (layout === 'centered-minimal') {
    return (
      <section className="px-6 py-32 text-center" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-3xl mx-auto">
          <H1 theme={theme} testid={tid('title')}>{c.title || 'Your Headline'}</H1>
          {c.tagline && <p data-testid={tid('tagline')} className="mt-6 text-lg md:text-xl opacity-70 max-w-xl mx-auto" style={{ color: theme.ink }}>{c.tagline}</p>}
          {buttons.length > 0 && <div className="mt-10 flex flex-wrap justify-center gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={theme} />)}</div>}
        </div>
      </section>
    );
  }

  if (layout === 'split-left' || layout === 'split-right') {
    const imgRight = layout === 'split-left' ? true : false;
    const Img = c.image_url ? <div className="aspect-[4/5] md:aspect-auto md:h-full"><SafeImg src={c.image_url} alt="" className="w-full h-full object-cover" /></div> : <div className="aspect-[4/5] bg-gradient-to-br from-gray-200 to-gray-400" />;
    const Txt = (
      <div className="px-8 md:px-16 py-16 md:py-24 flex flex-col justify-center" style={{ background: theme.bg, color: theme.ink }}>
        <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
        <H1 theme={theme} testid={tid('title')}>{c.title || 'Your Headline'}</H1>
        {c.tagline && <p data-testid={tid('tagline')} className="mt-6 text-lg opacity-75 max-w-md" style={{ color: theme.ink }}>{c.tagline}</p>}
        {buttons.length > 0 && <div className="mt-10 flex flex-wrap gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={theme} />)}</div>}
      </div>
    );
    return (
      <section className="grid grid-cols-1 md:grid-cols-2 min-h-[560px]">
        {imgRight ? Txt : Img}
        {imgRight ? Img : Txt}
      </section>
    );
  }

  if (layout === 'layered-editorial') {
    return (
      <section className="relative px-6 py-24 md:py-32 overflow-hidden" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6 relative z-10">
            <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H1 theme={theme} testid={tid('title')}>{c.title || 'Made with care.'}</H1>
            {c.tagline && <p data-testid={tid('tagline')} className="mt-6 text-lg opacity-75 max-w-md">{c.tagline}</p>}
            {buttons.length > 0 && <div className="mt-10 flex flex-wrap gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={theme} />)}</div>}
          </div>
          <div className="md:col-span-6 relative">
            {c.image_url && <SafeImg src={c.image_url} alt="" className="w-full h-[480px] object-cover" />}
            {c.secondary_image && <SafeImg src={c.secondary_image} alt="" className="absolute -bottom-8 -left-8 w-48 h-64 object-cover hidden md:block shadow-2xl" />}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'video-hero') {
    return (
      <section className="relative min-h-[640px] flex items-center justify-center text-center overflow-hidden">
        {c.video_url
          ? <video src={c.video_url} poster={c.poster} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
          : c.poster && <SafeImg src={c.poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-white max-w-4xl px-6">
          <H1 theme={{ ...theme, ink: '#fff' }} size="text-6xl md:text-8xl" testid={tid('title')}>{c.title || 'Always in motion.'}</H1>
          {c.tagline && <p data-testid={tid('tagline')} className="mt-6 text-xl opacity-90 whitespace-pre-line">{c.tagline}</p>}
          {buttons.length > 0 && <div className="mt-10 flex flex-wrap justify-center gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} sharp />)}</div>}
        </div>
      </section>
    );
  }

  // cinematic-fullwidth (default)
  return (
    <section className="relative min-h-[640px] flex items-center justify-center overflow-hidden">
      {c.image_url
        ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        : <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-black" />}
      <div className="absolute inset-0 bg-black/45" />
      <div className="relative z-10 text-center text-white max-w-4xl px-6">
        <H1 theme={{ ...theme, ink: '#fff' }} size="text-6xl md:text-7xl" testid={tid('title')}>{c.title || 'Your Story'}</H1>
        {c.tagline && <p data-testid={tid('tagline')} className="mt-6 text-xl opacity-90 whitespace-pre-line">{c.tagline}</p>}
        {buttons.length > 0 && <div className="mt-10 flex flex-wrap justify-center gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} sharp />)}</div>}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FEATURES — three-col / icon-cards / alternating-rows / stacked-list
// ─────────────────────────────────────────────────────────────────────────
function FeaturesBlock({ block, theme }) {
  const c = block.content || {};
  const items = c.items || [];
  const layout = block.layout || 'three-col-grid';
  const tid = _tid(block);

  // ── Iter 189c Phase B · Services — NEW `bento-grid` layout ──
  // Asymmetric 5-cell bento: 2 large tiles + 3 small tiles. Each tile has
  // an icon (or number), title, short description. Trendy in modern
  // SaaS/agency sites; a fresh alternative to icon-features-4up.
  if (layout === 'bento-grid') {
    const tiles = items.length >= 5 ? items : [...items, ...Array(Math.max(0, 5 - items.length)).fill({ title: 'Feature', text: 'Add copy here.' })].slice(0, 5);
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }} data-testid={tid('section')}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4 min-h-[520px]">
            <div className="md:col-span-2 md:row-span-2 p-8 flex flex-col justify-end" style={{ background: theme.accent + '10', borderRadius: theme.radius, border: `1px solid ${theme.ink}10` }}>
              <div className="text-5xl mb-4 opacity-70">{tiles[0].icon || '01'}</div>
              <h3 data-testid={tid('tile_0_title')} className="text-2xl font-bold" style={{ fontFamily: _f(theme.headingFont), color: theme.ink }}>{tiles[0].title}</h3>
              <p data-testid={tid('tile_0_text')} className="mt-2 opacity-75 leading-relaxed max-w-sm">{tiles[0].text}</p>
            </div>
            {tiles.slice(1, 5).map((it, i) => (
              <div key={i} className="p-6 flex flex-col justify-between" style={{ background: theme.bg, border: `1px solid ${theme.ink}12`, borderRadius: theme.radius }}>
                <div className="text-2xl opacity-70">{it.icon || `0${i + 2}`}</div>
                <div className="mt-6">
                  <h3 data-testid={tid(`tile_${i + 1}_title`)} className="text-base font-bold" style={{ fontFamily: _f(theme.headingFont), color: theme.ink }}>{it.title}</h3>
                  <p data-testid={tid(`tile_${i + 1}_text`)} className="mt-1 text-sm opacity-70 leading-relaxed">{it.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Services — NEW `featured-showcase` layout ──
  // Single hero service with big image right + benefits list + CTA left.
  // Ideal for agencies with one flagship offering.
  if (layout === 'featured-showcase') {
    const benefits = c.benefits || [];
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }} data-testid={tid('section')}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-14 items-center">
          <div>
            <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow || 'Signature service'}</Eyebrow>
            <H2 theme={theme} testid={tid('title')}>{c.title}</H2>
            {c.subtitle && <p data-testid={tid('subtitle')} className="mt-5 text-lg opacity-75 leading-relaxed">{c.subtitle}</p>}
            {benefits.length > 0 && (
              <ul className="mt-8 space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={tid(`benefit_${i}`)}>
                    <span className="mt-1 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: theme.accent, color: '#fff' }}>
                      <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
                    </span>
                    <span className="opacity-85">{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {c.cta_text && (
              <a href={c.cta_link || '#'} data-testid={tid('cta')} className="inline-block mt-10 px-7 py-3.5 rounded-full font-bold text-sm uppercase tracking-wider" style={{ background: theme.ink, color: theme.bg }}>
                {c.cta_text} →
              </a>
            )}
          </div>
          <div>
            <SafeImg src={c.image} alt="" className="w-full aspect-[4/5] object-cover" fallbackSeed={`featured-${block.id || ''}`} style={{ borderRadius: theme.radius }} />
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'alternating-rows') {
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
          </div>
          <div className="space-y-20">
            {items.map((it, i) => (
              <div key={i} className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'md:[&>div:first-child]:order-2' : ''}`}>
                <div><SafeImg src={it.image} alt="" className="w-full h-80 object-cover" fallbackSeed={`feature-${block.id || ''}-${i}`} /></div>
                <div>
                  <H2 theme={theme} testid={tid(`item_${i}_title`)}>{it.title}</H2>
                  <p data-testid={tid(`item_${i}_text`)} className="mt-4 text-lg opacity-75 leading-relaxed">{it.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
          {c.subtitle && <p data-testid={tid('subtitle')} className="mt-4 max-w-2xl mx-auto opacity-70">{c.subtitle}</p>}
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-${Math.min(items.length, 3) || 3} gap-8`}>
          {items.map((it, i) => (
            <div key={i} className="p-8" style={{ background: theme.bg, border: `1px solid ${theme.ink}15`, borderRadius: theme.radius }}>
              <div className="w-12 h-12 mb-5 flex items-center justify-center" style={{ background: theme.accent + '20', color: theme.accent, borderRadius: theme.radius }}>
                <span className="text-xl font-bold">{i + 1}</span>
              </div>
              <h3 data-testid={tid(`item_${i}_title`)} className="text-lg font-bold" style={{ fontFamily: _f(theme.headingFont), color: theme.ink }}>{it.title}</h3>
              <p data-testid={tid(`item_${i}_text`)} className="mt-2 text-sm opacity-75 leading-relaxed">{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SPLIT — image-left / image-right / overlapping-image / asymmetric
// ─────────────────────────────────────────────────────────────────────────
function SplitBlock({ block, theme }) {
  const c = block.content || {};
  const layout = block.layout || 'image-right';
  const imgRight = layout === 'image-right' || layout === 'asymmetric';
  const tid = _tid(block);
  const Img = (
    <div className="relative">
      {c.image_url && <SafeImg src={c.image_url} alt="" className="w-full h-[480px] md:h-[600px] object-cover" />}
      {c.second_image && layout === 'overlapping-image' && (
        <SafeImg src={c.second_image} alt="" className="absolute -bottom-8 -right-8 w-48 h-64 object-cover hidden md:block shadow-2xl" />
      )}
    </div>
  );
  const Txt = (
    <div className="px-8 md:px-16 py-16 flex flex-col justify-center">
      <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
      <H2 theme={theme} testid={tid('title')}>{c.title}</H2>
      <p data-testid={tid('text')} className="mt-6 text-lg opacity-75 max-w-md leading-relaxed">{c.text}</p>
      {(c.buttons || []).length > 0 && <div className="mt-8 flex flex-wrap gap-3">{c.buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={theme} />)}</div>}
    </div>
  );
  return (
    <section className="grid grid-cols-1 md:grid-cols-2" style={{ background: theme.bg, color: theme.ink }}>
      {imgRight ? Txt : Img}
      {imgRight ? Img : Txt}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// EDITORIAL — centered statement / two-col manifesto / pull-quote
// ─────────────────────────────────────────────────────────────────────────
function EditorialBlock({ block, theme }) {
  const c = block.content || {};
  const layout = block.layout || 'centered-statement';
  const tid = _tid(block);

  // Iter 189c Phase B — `editorial-magazine`: fashion-magazine hero with
  // Volume/Issue eyebrow + oversize serif title (italic-HTML aware) + hero
  // image on the right. Replaces the drift where this template used to
  // fall through to centered-statement (which ignored volume/issue/image).
  if (layout === 'editorial-magazine') {
    const titleHtml = String(c.title || 'The new <em>quiet</em> luxury.');
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }} data-testid={tid('section')}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] font-bold opacity-70">
              {c.volume && <span data-testid={tid('volume')}>{c.volume}</span>}
              {c.volume && c.issue && <span className="opacity-50">·</span>}
              {c.issue && <span data-testid={tid('issue')}>{c.issue}</span>}
            </div>
            <h1
              data-testid={tid('title')}
              className="mt-5 text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight [&_em]:italic [&_em]:font-normal"
              style={{ fontFamily: '"Playfair Display", Georgia, serif', color: theme.ink }}
              dangerouslySetInnerHTML={{ __html: titleHtml }}
            />
            {c.tagline && <p data-testid={tid('tagline')} className="mt-6 text-base opacity-75 leading-relaxed max-w-md">{c.tagline}</p>}
          </div>
          {c.image_url && (
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={c.image_url} alt="" className="w-full h-full object-cover" data-testid={tid('image')} />
            </div>
          )}
        </div>
      </section>
    );
  }

  if (layout === 'pull-quote') {
    return (
      <section className="px-6 py-32 text-center" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-4xl mx-auto">
          <p data-testid={tid('quote')} className="text-3xl md:text-5xl font-bold leading-snug" style={{ fontFamily: _f(theme.headingFont) }}>&ldquo;{c.quote}&rdquo;</p>
          <p data-testid={tid('author')} className="mt-10 text-sm uppercase tracking-[0.2em] opacity-70">— {c.author}{c.role && `, ${c.role}`}</p>
        </div>
      </section>
    );
  }

  if (layout === 'two-column-manifesto') {
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H2 theme={theme} testid={tid('title')}>{c.title}</H2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {(c.columns || []).map((col, i) => (
              <div key={i}>
                <p data-testid={tid(`col_${i}_label`)} className="text-sm font-bold tracking-widest opacity-50">{col.label}</p>
                <h3 data-testid={tid(`col_${i}_title`)} className="mt-3 text-2xl font-bold" style={{ fontFamily: _f(theme.headingFont) }}>{col.title}</h3>
                <p data-testid={tid(`col_${i}_text`)} className="mt-4 opacity-75 leading-relaxed">{col.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-32 text-center" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-3xl mx-auto">
        <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
        <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
        <p data-testid={tid('text')} className="mt-8 text-lg md:text-xl opacity-80 leading-relaxed">{c.text}</p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// STATS — kpi-row / kpi-cards / centered-counter
// ─────────────────────────────────────────────────────────────────────────
function StatsBlock({ block, theme }) {
  const c = block.content || {};
  const items = c.items || [];
  const layout = block.layout || 'kpi-row';
  const isCards = layout === 'kpi-cards';
  const tid = _tid(block);

  return (
    <section className="px-6 py-20" style={{ background: isCards ? theme.bg : theme.deep, color: isCards ? theme.ink : '#fff' }}>
      <div className="max-w-6xl mx-auto">
        {c.title && (
          <div className="text-center mb-12">
            <Eyebrow theme={{ ...theme, ink: isCards ? theme.ink : '#fff' }} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: _f(theme.headingFont) }}>{c.title}</h2>
          </div>
        )}
        <div className={`grid grid-cols-2 md:grid-cols-${Math.min(items.length, 4) || 4} gap-8`}>
          {items.map((s, i) => (
            <div key={i} className={isCards ? 'p-6 text-center' : 'text-center'} style={isCards ? { background: theme.bg, border: `1px solid ${theme.ink}15`, borderRadius: theme.radius } : {}}>
              <p data-testid={tid(`item_${i}_value`)} className="text-4xl md:text-6xl font-bold" style={{ fontFamily: _f(theme.headingFont), color: isCards ? theme.accent : theme.accent }}>{s.value}</p>
              <p data-testid={tid(`item_${i}_label`)} className="mt-2 text-sm opacity-80">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LOGOS — logo-strip / logo-grid
// ─────────────────────────────────────────────────────────────────────────
function LogosBlock({ block, theme }) {
  const c = block.content || {};
  const logos = c.logos || [];
  const tid = _tid(block);
  return (
    <section className="px-6 py-14" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto text-center">
        {c.eyebrow && <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>}
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14 mt-5 opacity-60">
          {logos.map((l, i) => (
            <span key={i} data-testid={tid(`logo_${i}_name`)} className="text-xl font-bold tracking-wide" style={{ fontFamily: _f(theme.headingFont) }}>{l.name}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TEAM — card-grid / founder-spotlight / circle-row
// ─────────────────────────────────────────────────────────────────────────
function TeamBlock({ block, theme }) {
  const c = block.content || {};
  const members = c.members || [];
  const tid = _tid(block);
  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
          {c.subtitle && <p data-testid={tid('subtitle')} className="mt-4 opacity-70 max-w-2xl mx-auto">{c.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {members.map((m, i) => (
            <div key={i} className="text-center">
              {m.image && <SafeImg src={m.image} alt={m.name} className="w-full aspect-square object-cover" style={{ borderRadius: theme.radius }} />}
              <p data-testid={tid(`member_${i}_name`)} className="mt-4 font-bold" style={{ fontFamily: _f(theme.headingFont) }}>{m.name}</p>
              <p data-testid={tid(`member_${i}_role`)} className="text-xs uppercase tracking-widest opacity-60 mt-1">{m.role}</p>
              {m.bio && <p data-testid={tid(`member_${i}_bio`)} className="mt-3 text-sm opacity-70">{m.bio}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SERVICES — three-column-cards / pricing-cards / alternating-feature
// ─────────────────────────────────────────────────────────────────────────
function ServicesBlock({ block, theme }) {
  const c = block.content || {};
  const items = c.items || [];
  const bid = block.id;
  const inlineTid = (f) => `inline-${bid}-${f}`;
  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <Eyebrow theme={theme}>{c.eyebrow}</Eyebrow>
          <H2 theme={theme} className="text-center">{c.title}</H2>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-${Math.min(items.length, 3) || 3} gap-6`}>
          {items.map((s, i) => (
            <div key={i} className="p-8 flex flex-col" style={{ background: theme.bg, border: `1px solid ${theme.ink}15`, borderRadius: theme.radius }}>
              <h3 data-testid={inlineTid(`item_${i}_title`)} className="text-xl font-bold" style={{ fontFamily: _f(theme.headingFont) }}>{s.title}</h3>
              {s.price && <p data-testid={inlineTid(`item_${i}_price`)} className="mt-3 text-3xl font-bold" style={{ color: theme.accent, fontFamily: _f(theme.headingFont) }}>{s.price}</p>}
              {s.duration && <p className="text-xs uppercase tracking-widest opacity-60 mt-1">{s.duration}</p>}
              {s.description && <p className="mt-4 text-sm opacity-75 leading-relaxed">{s.description}</p>}
              {(s.features || []).length > 0 && (
                <ul className="mt-5 space-y-2 text-sm">
                  {s.features.map((f, j) => <li key={j} className="flex items-start gap-2"><span style={{ color: theme.accent }}>✓</span>{f}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DIRECTORY — vendor-grid / vendor-list / category-tiles
// ─────────────────────────────────────────────────────────────────────────
function DirectoryBlock({ block, theme }) {
  const c = block.content || {};
  const names = ['Finest Feathers', 'Stone & Cedar Co.', 'Northern Quill', 'Atelier Mishomis', 'River Beadworks', 'Bear Paw Studio', 'Wapi Apparel', 'Mountain Loom'];
  const tags = ['Beadwork', 'Photography', 'Apparel', 'Wellness', 'Music', 'Art', 'Catering', 'Design'];
  const photos = c.sample_photos || c._sample_photos || [];
  const tid = _tid(block);
  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
          {c.subtitle && <p data-testid={tid('subtitle')} className="mt-4 opacity-70 max-w-2xl mx-auto">{c.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
            <div key={i} className="overflow-hidden" style={{ background: theme.soft, borderRadius: theme.radius }}>
              {photos[i] ? (
                <SafeImg src={photos[i]} alt={names[i]} className="w-full aspect-square object-cover" />
              ) : (
                <div className="aspect-square" style={{ background: `linear-gradient(135deg, ${theme.accent}22, ${theme.deep}55)` }} />
              )}
              <div className="p-3">
                <p className="text-sm font-bold truncate" style={{ fontFamily: _f(theme.headingFont) }}>{names[i]}</p>
                <p className="text-xs opacity-60 truncate mt-1">{tags[i]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TESTIMONIALS — three-up / carousel / stacked / quote-banner
// ─────────────────────────────────────────────────────────────────────────
function TestimonialsBlock({ block, theme }) {
  const c = block.content || {};
  const items = c.items || [];
  const layout = block.layout || 'three-up';
  const tid = _tid(block);

  if (layout === 'quote-banner') {
    const q = items[0] || {};
    return (
      <section className="px-6 py-24 text-center" style={{ background: theme.deep, color: '#fff' }}>
        <div className="max-w-4xl mx-auto">
          <p data-testid={tid('item_0_quote')} className="text-2xl md:text-4xl font-bold leading-snug" style={{ fontFamily: _f(theme.headingFont) }}>&ldquo;{q.quote}&rdquo;</p>
          <p data-testid={tid('item_0_author')} className="mt-8 text-sm uppercase tracking-[0.2em] opacity-80">— {q.author}{q.role && `, ${q.role}`}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <div key={i} className="p-8" style={{ background: theme.bg, border: `1px solid ${theme.ink}15`, borderRadius: theme.radius }}>
              <p data-testid={tid(`item_${i}_quote`)} className="text-sm leading-relaxed italic" style={{ fontFamily: _f(theme.headingFont) }}>&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-6 flex items-center gap-3">
                {t.image && <SafeImg src={t.image} alt={t.author} className="w-10 h-10 rounded-full object-cover" />}
                <div>
                  <p data-testid={tid(`item_${i}_author`)} className="text-sm font-bold">{t.author}</p>
                  <p data-testid={tid(`item_${i}_role`)} className="text-xs opacity-60">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// GALLERY — masonry / horizontal-scroll / grid-uniform / lookbook
// ─────────────────────────────────────────────────────────────────────────
function GalleryBlock({ block, theme }) {
  const c = block.content || {};
  const images = c.images || [];
  const layout = block.layout || 'grid-uniform';
  const tid = _tid(block);

  if (layout === 'lookbook') {
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12"><Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow><H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {images.slice(0, 9).map((img, i) => (
              <SafeImg key={i} src={img} alt="" className={`w-full object-cover ${i % 4 === 0 ? 'aspect-[3/4] md:row-span-2 md:h-full' : 'aspect-square'}`} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'masonry') {
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12"><Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow><H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2></div>
          <div className="columns-1 md:columns-3 gap-3">
            {images.map((img, i) => (
              <SafeImg key={i} src={img} alt="" className="w-full mb-3 break-inside-avoid" style={{ borderRadius: theme.radius }} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12"><Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow><H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img, i) => <SafeImg key={i} src={img} alt="" className="w-full aspect-square object-cover" style={{ borderRadius: theme.radius }} />)}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TIMELINE — vertical / horizontal
// ─────────────────────────────────────────────────────────────────────────
function TimelineBlock({ block, theme }) {
  const c = block.content || {};
  const events = c.events || [];
  const horizontal = block.layout === 'horizontal';
  const tid = _tid(block);

  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14"><Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow><H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2></div>
        <div className={horizontal ? 'grid grid-cols-1 md:grid-cols-3 gap-6' : 'space-y-12'}>
          {events.map((e, i) => (
            <div key={i} className={horizontal ? '' : 'grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6'}>
              <p data-testid={tid(`event_${i}_label`)} className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: theme.accent }}>{e.label}</p>
              <div>
                <h3 data-testid={tid(`event_${i}_title`)} className="text-xl font-bold" style={{ fontFamily: _f(theme.headingFont) }}>{e.title}</h3>
                <p data-testid={tid(`event_${i}_text`)} className="mt-2 opacity-75 leading-relaxed">{e.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// EVENTS — event-list / event-cards / calendar
// ─────────────────────────────────────────────────────────────────────────
function EventsBlock({ block, theme }) {
  const c = block.content || {};
  const items = c.items || [];
  const tid = _tid(block);
  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12"><Eyebrow theme={theme} testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow><H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((ev, i) => (
            <div key={i} className="overflow-hidden" style={{ background: theme.bg, border: `1px solid ${theme.ink}15`, borderRadius: theme.radius }}>
              {ev.image && <SafeImg src={ev.image} alt="" className="w-full aspect-[4/3] object-cover" />}
              <div className="p-5">
                <p data-testid={tid(`item_${i}_date`)} className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: theme.accent }}>{ev.date}</p>
                <h3 data-testid={tid(`item_${i}_title`)} className="mt-2 text-lg font-bold" style={{ fontFamily: _f(theme.headingFont) }}>{ev.title}</h3>
                <p data-testid={tid(`item_${i}_location`)} className="mt-1 text-sm opacity-60">{ev.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FORM — contact / newsletter / application / donate / booking
// ─────────────────────────────────────────────────────────────────────────
function FormBlock({ block, theme }) {
  const c = block.content || {};
  const fields = c.fields || [];
  const tid = _tid(block);
  return (
    <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10"><H2 theme={theme} className="text-center" testid={tid('title')}>{c.title}</H2>{c.subtitle && <p data-testid={tid('subtitle')} className="mt-3 opacity-70">{c.subtitle}</p>}</div>
        <div className="space-y-3">
          {fields.map((f, i) => (
            f.type === 'textarea'
              ? <div key={i} className="w-full px-4 py-4 text-sm opacity-60" style={{ border: `1px solid ${theme.ink}25`, borderRadius: theme.radius, minHeight: 120 }}>{f.label}{f.required && ' *'}</div>
              : <div key={i} className="w-full px-4 py-3 text-sm opacity-60" style={{ border: `1px solid ${theme.ink}25`, borderRadius: theme.radius }}>{f.label}{f.required && ' *'}</div>
          ))}
          <button className="w-full px-7 py-4 text-sm font-bold tracking-wider uppercase mt-4" style={{ background: theme.accent, color: '#fff', borderRadius: theme.radius }}>{c.submit_label || 'Send'}</button>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CTA — large-banner / split-cta / image-cta / floating-bar
// ─────────────────────────────────────────────────────────────────────────
function CtaBlock({ block, theme }) {
  const c = block.content || {};
  const buttons = c.buttons || [];
  const layout = block.layout || 'large-banner';
  const tid = _tid(block);

  if (layout === 'image-cta' && c.image_url) {
    return (
      <section className="relative min-h-[480px] flex items-center justify-center overflow-hidden">
        <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center text-white max-w-3xl px-6">
          <H1 theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-5xl" testid={tid('title')}>{c.title}</H1>
          {c.subtitle && <p data-testid={tid('subtitle')} className="mt-5 text-lg opacity-90">{c.subtitle}</p>}
          {buttons.length > 0 && <div className="mt-8 flex flex-wrap justify-center gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} sharp />)}</div>}
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-24 text-center" style={{ background: theme.deep, color: '#fff' }}>
      <div className="max-w-3xl mx-auto">
        <H1 theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-5xl" testid={tid('title')}>{c.title}</H1>
        {c.subtitle && <p data-testid={tid('subtitle')} className="mt-5 text-lg opacity-85">{c.subtitle}</p>}
        {buttons.length > 0 && <div className="mt-10 flex flex-wrap justify-center gap-3">{buttons.map((b, i) => <Btn key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={b.color} theme={{ ...theme, accent: theme.accent, ink: '#fff' }} />)}</div>}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MARQUEE — extended (legacy renderer handles basic; premium uses richer config)
// ─────────────────────────────────────────────────────────────────────────
function MarqueeBlock({ block, theme }) {
  const c = block.content || {};
  const words = c.words || [];
  const sep = c.separator === 'dot' ? ' · ' : c.separator === 'star' ? ' ★ ' : c.separator === 'dash' ? ' — ' : c.separator === 'slash' ? ' / ' : ' · ';
  const size = { sm: 'text-base', md: 'text-lg', lg: 'text-2xl', xl: 'text-3xl', '2xl': 'text-5xl', '3xl': 'text-7xl' }[c.font_size] || 'text-2xl';
  return (
    <div className="overflow-hidden whitespace-nowrap py-5" style={{ background: theme.ink, color: theme.bg }}>
      <p className={`${size} font-bold tracking-tight`} style={{ fontFamily: _f(theme.headingFont) }}>{words.join(sep)}{sep}{words.join(sep)}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────
const PREMIUM_TYPES = new Set([
  'features', 'split', 'editorial', 'stats', 'logos', 'team', 'services',
  'directory', 'testimonials', 'timeline', 'events', 'cta',
]);

// Phase 1+2 extensions (Iteration 141) — new block types + new layout variants.
// We call renderExt FIRST so new layout IDs win; if it returns null we fall
// back to the legacy switch below for default layouts.
import { renderExt as renderExtVariants, EXT_BLOCK_TYPES } from './PremiumSectionRendererExt';

/**
 * Try to render a block using the premium renderer.
 * Returns React element or null.
 *
 * For `hero`, `gallery`, `marquee`, `form` we ONLY take over when the block
 * has a `layout` field matching a premium layout (so legacy blocks keep their
 * legacy look-and-feel).
 */
export function renderPremiumSection(block, palette, headingFont, radius, onEdit, onMediaPick) {
  if (!block || !block.type) return null;
  const theme = resolveTheme(block, palette, headingFont, radius);
  const t = block.type;

  // ── Phase 1+2 extensions take first shot (new block types + new layouts) ──
  const ext = renderExtVariants(block, theme, onEdit, onMediaPick);
  if (ext) return ext;
  // If the block type is brand-new (data_table, product_grid, etc.) but the
  // ext returned null for some reason, still let it be claimed so we don't
  // fall through to legacy rendering.
  if (EXT_BLOCK_TYPES.has(t)) return null;

  if (PREMIUM_TYPES.has(t)) {
    switch (t) {
      case 'features':     return <FeaturesBlock block={block} theme={theme} />;
      case 'split':        return <SplitBlock block={block} theme={theme} />;
      case 'editorial':    return <EditorialBlock block={block} theme={theme} />;
      case 'stats':        return <StatsBlock block={block} theme={theme} />;
      case 'logos':        return <LogosBlock block={block} theme={theme} />;
      case 'team':         return <TeamBlock block={block} theme={theme} />;
      case 'services':     return <ServicesBlock block={block} theme={theme} />;
      case 'directory':    return <DirectoryBlock block={block} theme={theme} />;
      case 'testimonials': return <TestimonialsBlock block={block} theme={theme} />;
      case 'timeline':     return <TimelineBlock block={block} theme={theme} />;
      case 'events':       return <EventsBlock block={block} theme={theme} />;
      case 'cta':          return <CtaBlock block={block} theme={theme} />;
      default: return null;
    }
  }

  // Premium-only when block has a known premium layout.
  // Iter 175 — removed 'video-hero' from the premium claim list: that
  // layout is now owned by our IntroShapes-matched `/blocks/HeroBlock.js`,
  // which renders the play-button + image-poster look the preview tile
  // shows. The legacy premium video-hero used c.video_url (which our
  // intro templates don't ship), producing a black box on the canvas.
  if (t === 'hero' && ['layered-editorial', 'split-left', 'split-right', 'centered-minimal'].includes(block.layout)) {
    return <HeroBlock block={block} theme={theme} />;
  }
  if (t === 'gallery' && ['masonry', 'lookbook'].includes(block.layout)) {
    return <GalleryBlock block={block} theme={theme} />;
  }
  if (t === 'form' && ['contact', 'newsletter', 'application', 'donate', 'booking'].includes(block.layout)) {
    return <FormBlock block={block} theme={theme} />;
  }
  if (t === 'marquee' && block.layout === 'scrolling') {
    return <MarqueeBlock block={block} theme={theme} />;
  }
  return null;
}

export const PREMIUM_SECTION_TYPES = PREMIUM_TYPES;
